import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Autocomplete from '@mui/material/Autocomplete';
import OptimizedCityAutocomplete from '../../../components/OptimizedCityAutocomplete';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getActiveAdmins } from '../../../store/slices/usersSlice';
import { PUT, GET, POST, DELETE } from '../../../configs/api';
import { api, states } from '../../../utils/constants';
import { updateClient } from '../../../store/slices/clientsSlice';
import {
  canManageClientsDirectly,
  canManageClientPocs,
  canViewClientPocs,
  isManagement,
} from '../../../utils/permissions';
import { useTheme } from '@mui/material/styles';
import useIsMobile from '../../../hooks/useIsMobile';
import PocFormDialog from './PocFormDialog';
import ActivityTab from './tabs/ActivityTab';
import ClientEarningsSummaryPanel from './earnings/ClientEarningsSummaryPanel';
import ClientPaidUnpaidPanel from './earnings/ClientPaidUnpaidPanel';
import ClientJobsStatusChips from './earnings/ClientJobsStatusChips';
import ClientJobsDrilldownDialog from './earnings/ClientJobsDrilldownDialog';
import { formatEarningsRangeLabel, formatLocalDateYmd } from './earnings/clientEarningsUtils';
import JobDetailsDialog from '../jobs/JobDetailsDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Chip from '@mui/material/Chip';
import DateRangePickerDropdown from '../../../components/DateRangePickerDropdown';
import { getClientColor, setClientColor, getClientChipStylesFromHex } from '../../../utils/clientColors';
import ClientInvoicePatternField from './ClientInvoicePatternField';
import { EDIT_CLIENT_FIELD_ORDER, useClientFormValidation } from '../../../utils/clientFormValidation';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Phone formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  } else if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

// Zip code formatting function
const formatZipCode = (value) => {
  // Remove all non-numeric characters
  const zipCode = value.replace(/\D/g, '');
  
  // Format as 12345-6789 (5+4 format)
  if (zipCode.length > 5) {
    const firstPart = zipCode.substring(0, 5);
    const secondPart = zipCode.substring(5, 9);
    return `${firstPart}-${secondPart}`;
  }
  return zipCode;
};

const emptyClient = {id: '',client_name: '',client_status: 1,phone: '',city: '',state: '',zip_code: '',display_as: '',client_address: '',address_2: '',email: '',invoice_pattern: '',admins: [],latitude: null,longitude: null};

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, contentRef, keepMounted = false, ...other } = props;
  const isActive = value === index;
  const shouldRender = isActive || keepMounted;

  return (
    <div
      role="tabpanel"
      // Avoid `hidden` when keepMounted — display:none zeros offsetHeight and breaks measuring
      hidden={!isActive && !keepMounted}
      aria-hidden={!isActive}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {shouldRender && (
        <Box
          ref={contentRef}
          sx={{
            p: 3,
            ...(!isActive && keepMounted
              ? {
                  position: 'absolute',
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  top: 0,
                  left: 0,
                  right: 0,
                }
              : {}),
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `client-tab-${index}`,
    'aria-controls': `client-tabpanel-${index}`,
  };
}

export default function ClientDetailsDialog({ selectedClient, setSelectedClient, user }) {
  const theme = useTheme();
  const isSmallScreen = useIsMobile();
  const isDarkMode = theme.palette.mode === 'dark';
  const showPocTab = canViewClientPocs(user);
  const showActivityTab = isManagement(user);
  const earningsJobsTabIndex = showPocTab ? 2 : 1;
  const activityTabIndex = earningsJobsTabIndex + 1;
  const dispatch = useDispatch();
  const [editClient, setEditClient] = useState(emptyClient);
  const [editMode, setEditMode] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [activitiesCount, setActivitiesCount] = useState(0);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [activityRefreshToken, setActivityRefreshToken] = useState(0);
  const admins = useSelector(getActiveAdmins);
  const [showError, setShowError] = useState('');
  const {
    errors,
    formRef,
    handleFieldChange,
    handleFieldBlur,
    revalidateFields,
    validateAll,
    resetValidation,
    applyDuplicateError,
  } = useClientFormValidation({
    includeRequestNotes: false,
    fieldOrder: EDIT_CLIENT_FIELD_ORDER,
  });
  const editClientRef = useRef(editClient);
  editClientRef.current = editClient;
  const displayAsLinkedRef = useRef(true);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [earningsOverview, setEarningsOverview] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(() => Boolean(selectedClient?.id));
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [earningsError, setEarningsError] = useState('');
  const [drilldownMetric, setDrilldownMetric] = useState(null);
  const [drilldownStatusId, setDrilldownStatusId] = useState(null);
  const [drilldownStatusLabel, setDrilldownStatusLabel] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [pocs, setPocs] = useState([]);
  const [loadingPocs, setLoadingPocs] = useState(false);
  const [pocsError, setPocsError] = useState('');
  const [pocFormOpen, setPocFormOpen] = useState(false);
  const [editingPoc, setEditingPoc] = useState(null);
  const [savingPoc, setSavingPoc] = useState(false);
  const [showPocSuccessToast, setShowPocSuccessToast] = useState(false);
  const [clientColorHex, setClientColorHex] = useState('#6b7280');
  const generalInfoContentRef = useRef(null);
  const [lockedContentHeight, setLockedContentHeight] = useState(null);

  useEffect(() => {
    resetValidation();
    setShowError('');
    const current = editClientRef.current;
    displayAsLinkedRef.current = (current.display_as || '') === (current.client_name || '');
  }, [selectedClient?.id, editMode, resetValidation]);

  // Keep form in sync when the selected client object updates (e.g. after save).
  useEffect(() => {
    setEditClient(selectedClient ? { ...selectedClient } : emptyClient);
    setClientColorHex(
      selectedClient?.id != null ? getClientColor(selectedClient.id, user?.company_id) : '#6b7280',
    );
  }, [selectedClient, user?.company_id]);

  // Reset tab/filters/activity only when switching to a different client.
  useEffect(() => {
    setTabValue(0);
    setLockedContentHeight(null);
    setDateStart(null);
    setDateEnd(null);
    setEarningsOverview(null);
    setEarningsError('');
    setLoadingEarnings(Boolean(selectedClient?.id));
    setDrilldownMetric(null);
    setDrilldownStatusId(null);
    setDrilldownStatusLabel(null);
    setSelectedJob(null);
    setPocs([]);
    setPocsError('');
    setActivitiesCount(0);
    setActivityLogs([]);
    setActivityLoading(false);
    setActivityError('');
    setActivityRefreshToken(0);
  }, [selectedClient?.id]);

  // Lock tab content height to General Info (desktop only); mobile uses flex fill
  useLayoutEffect(() => {
    if (isSmallScreen || !selectedClient) {
      setLockedContentHeight(null);
      return;
    }

    const el = generalInfoContentRef.current;
    if (!el) return;

    const height = el.offsetHeight;
    if (height > 0) {
      setLockedContentHeight((prev) => (prev === height ? prev : height));
    }
  }, [
    tabValue,
    selectedClient,
    editMode,
    isSmallScreen,
    editClient?.address_2,
    editClient?.admins?.length,
  ]);

  const handleTabChange = (event, newValue) => {
    // Edit mode only applies to General Info — leave it cleanly when switching tabs
    if (editMode && newValue !== 0) {
      setEditClient(selectedClient ? { ...selectedClient } : emptyClient);
      setEditMode(false);
      resetValidation();
      setShowError('');
    }
    setTabValue(newValue);
  };

  const showGeneralInfoActions = tabValue === 0;

  // Fetch POCs for the client when the user can view them
  useEffect(() => {
    const fetchClientPocs = async () => {
      if (!selectedClient?.id) return;

      if (!canViewClientPocs(user)) {
        setPocs([]);
        setPocsError('');
        setLoadingPocs(false);
        return;
      }

      setLoadingPocs(true);
      setPocsError('');

      try {
        const { status, data, error, message } = await GET(`${api.getClientPocs}/${selectedClient.id}/pocs`);

        if (error || status !== 200) {
          setPocsError(message || 'Failed to fetch POCs');
          setPocs([]);
        } else {
          setPocs(data?.pocs || []);
        }
      } catch (error) {
        setPocsError('Failed to fetch POCs. Please try again later.');
        setPocs([]);
      } finally {
        setLoadingPocs(false);
      }
    };

    fetchClientPocs();
  }, [selectedClient?.id, user]);

  const handleAddPoc = () => {
    setEditingPoc(null);
    setPocFormOpen(true);
  };

  const handleEditPoc = (poc) => {
    setEditingPoc(poc);
    setPocFormOpen(true);
  };

  const handleDeletePoc = async (poc) => {
    if (!window.confirm(`Are you sure you want to delete ${poc.name}?`)) {
      return;
    }

    try {
      const { status, error, message } = await DELETE(`${api.deleteClientPoc}/${selectedClient.id}/pocs/${poc.id}`);

      if (error || status !== 200) {
        setPocsError(message || 'Failed to delete POC');
      } else {
        setPocs(pocs.filter(p => p.id !== poc.id));
        setShowPocSuccessToast(true);
        setActivityRefreshToken((token) => token + 1);
      }
    } catch (error) {
      setPocsError('Failed to delete POC. Please try again later.');
    }
  };

  const handleSavePoc = async (pocData) => {
    setSavingPoc(true);
    try {
      if (editingPoc) {
        // Update existing POC
        const { status, data, error, message } = await PUT(
          `${api.updateClientPoc}/${selectedClient.id}/pocs/${editingPoc.id}`,
          {},
          pocData
        );

        if (error || status !== 200) {
          setPocsError(message || 'Failed to update POC');
        } else {
          setPocs(pocs.map(p => p.id === editingPoc.id ? data : p));
          setPocFormOpen(false);
          setEditingPoc(null);
          setShowPocSuccessToast(true);
          setActivityRefreshToken((token) => token + 1);
        }
      } else {
        // Create new POC
        const { status, data, error, message } = await POST(
          `${api.createClientPoc}/${selectedClient.id}/pocs`,
          {},
          pocData
        );

        if (error || status !== 201) {
          setPocsError(message || 'Failed to create POC');
        } else {
          setPocs([...pocs, data]);
          setPocFormOpen(false);
          setShowPocSuccessToast(true);
          setActivityRefreshToken((token) => token + 1);
        }
      }
    } catch (error) {
      setPocsError('Failed to save POC. Please try again later.');
    } finally {
      setSavingPoc(false);
    }
  };

  // Prefetch activity when the dialog opens so the badge/count is ready before the tab is visited
  useEffect(() => {
    let cancelled = false;

    const fetchClientActivity = async () => {
      if (!showActivityTab || !selectedClient?.id) {
        setActivityLogs([]);
        setActivitiesCount(0);
        setActivityError('');
        setActivityLoading(false);
        return;
      }

      setActivityLoading(true);
      setActivityError('');
      const { data, status, message } = await GET(api.getClientLogs(selectedClient.id));
      if (cancelled) return;

      if (status === 200) {
        const nextLogs = Array.isArray(data) ? data : [];
        setActivityLogs(nextLogs);
        setActivitiesCount(nextLogs.length);
        setActivityError('');
      } else {
        // Keep last good logs/badge; surface the error without wiping prior data
        setActivityError(message || 'Failed to load activity.');
      }
      setActivityLoading(false);
    };

    fetchClientActivity();
    return () => {
      cancelled = true;
    };
  }, [selectedClient?.id, activityRefreshToken, showActivityTab]);

  // Fetch earnings overview when client or date filters change
  useEffect(() => {
    if (!selectedClient?.id) {
      setLoadingEarnings(false);
      setEarningsOverview(null);
      setEarningsError('');
      return undefined;
    }

    let cancelled = false;
    const fetchEarningsOverview = async () => {
      setLoadingEarnings(true);
      setEarningsError('');

      try {
        const params = {};
        const startYmd = formatLocalDateYmd(dateStart);
        const endYmd = formatLocalDateYmd(dateEnd);
        if (startYmd) params.day_start = startYmd;
        if (endYmd) params.day_end = endYmd;

        const { status, data, error, message } = await GET(
          `${api.getClientEarningsOverview}/${selectedClient.id}/earnings-overview`,
          params,
        );

        if (cancelled) return;

        if (error || status !== 200) {
          setEarningsError(message || 'Failed to fetch earnings overview');
          setEarningsOverview(null);
        } else {
          setEarningsOverview(data || null);
        }
      } catch (error) {
        if (cancelled) return;
        setEarningsError('Failed to fetch earnings overview. Please try again later.');
        setEarningsOverview(null);
      } finally {
        if (!cancelled) setLoadingEarnings(false);
      }
    };

    fetchEarningsOverview();
    return () => {
      cancelled = true;
    };
  }, [selectedClient?.id, dateStart, dateEnd]);

  const earningsQueryParams = useMemo(() => {
    const params = {};
    const startYmd = formatLocalDateYmd(dateStart);
    const endYmd = formatLocalDateYmd(dateEnd);
    if (startYmd) params.day_start = startYmd;
    if (endYmd) params.day_end = endYmd;
    return params;
  }, [dateStart, dateEnd]);

  const earningsRangeLabel = useMemo(
    () => formatEarningsRangeLabel(dateStart, dateEnd),
    [dateStart, dateEnd],
  );

  const closeDrilldown = useCallback(() => {
    setDrilldownMetric(null);
    setDrilldownStatusId(null);
    setDrilldownStatusLabel(null);
  }, []);

  const handleEarningsMetricClick = useCallback((metric) => {
    setDrilldownStatusId(null);
    setDrilldownStatusLabel(null);
    setDrilldownMetric(metric);
  }, []);

  const handleEarningsStatusClick = useCallback((statusId, label) => {
    setDrilldownMetric(null);
    setDrilldownStatusId(statusId);
    setDrilldownStatusLabel(label || null);
  }, []);

  const handleOpenJobFromEarnings = useCallback((job) => {
    if (!job?.id) return;
    setSelectedJob({ id: job.id });
  }, []);

  const updateEditField = (field, value) => {
    if (!editMode) return;
    if (field === 'display_as') {
      displayAsLinkedRef.current = value === (editClientRef.current.client_name || '');
    }
    const shouldSyncDisplayAs = field === 'client_name' && displayAsLinkedRef.current;

    setEditClient((prev) => {
      const next = { ...prev, [field]: value };
      if (shouldSyncDisplayAs) {
        next.display_as = value;
      }
      editClientRef.current = next;
      return next;
    });
    handleFieldChange(field, value);
    if (shouldSyncDisplayAs) {
      handleFieldChange('display_as', value);
    }
  };

  const handlePlaceSelect = (addressData) => {
    const formattedZip = (addressData.zipCode && addressData.zipCode.trim())
      ? formatZipCode(addressData.zipCode)
      : '';
    const prev = editClientRef.current;
    const next = {
      ...prev,
      client_address: addressData.address || prev.client_address,
      city: (addressData.city && addressData.city.trim()) ? addressData.city : '',
      state: (addressData.state && addressData.state.trim()) ? addressData.state : '',
      zip_code: formattedZip,
      latitude: addressData.latitude !== null && addressData.latitude !== undefined ? addressData.latitude : null,
      longitude: addressData.longitude !== null && addressData.longitude !== undefined ? addressData.longitude : null,
    };
    editClientRef.current = next;
    setEditClient(next);
    revalidateFields(next, ['client_address', 'city', 'state', 'zip_code']);
  };

  if (!selectedClient) return null;

  const handleUpdateClient = async () => {
    setShowError('');
    if (!validateAll(editClientRef.current)) {
      setTabValue(0);
      return;
    }

    setLoading(true);
    const { status, data, message } = await PUT(api.updateClient, {}, { ...editClient, admins: editClient.admins.map(admin => admin.id) });
    if (status === 200) {
      setEditMode(false);
      setShowSuccessToast(true);
      resetValidation();
      dispatch(updateClient({ ...editClient, ...(data || {}) }));
      setActivityRefreshToken((token) => token + 1);
    } else if (status === 409) {
      setShowError(applyDuplicateError(data?.message || message));
    } else {
      setShowError(data?.message || message || 'Something went wrong, please try again later.');
    }
    setLoading(false);
  }

  return (
    <>
    <Dialog
      open={!!selectedClient}
      onClose={() => {
        setSelectedClient(null); 
        setEditMode(false); 
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            width: isSmallScreen ? '100%' : '60vw',
            maxWidth: isSmallScreen ? '100%' : 'none',
            maxHeight: isSmallScreen ? '100%' : '90vh',
            height: isSmallScreen ? '100%' : undefined,
            m: isSmallScreen ? 0 : undefined,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        }
      }}
    >
      {/* Header */}
      <Box 
        id="draggable-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.paper',
          cursor: isSmallScreen ? 'default' : 'move',
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: 'text.primary',
          fontSize: '1.25rem',
          fontFamily: 'inherit'
        }}>
          {editClient.client_name || 'Edit Client'}
        </Typography>
        <IconButton 
          onClick={() => {
            setSelectedClient(null); 
            setEditMode(false); 
          }} 
          sx={{ 
            color: 'text.secondary',
            padding: '4px',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ 
        px: 2,
        pt: 1,
        pb: 1,
        flexShrink: 0,
      }}>
        <Box sx={{
          display: 'inline-flex',
          px: 1.5,
          py: 0.75,
          backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
          borderRadius: '8px 8px 0 0',
          boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
          overflow: 'visible',
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="client details tabs"
            sx={{
              minHeight: 'auto',
              overflow: 'visible',
              '& .MuiTabs-scroller': {
                overflow: 'visible !important',
              },
              '& .MuiTabs-indicator': {
                display: 'none'
              },
              '& .MuiTabs-scrollButtons': {
                color: 'text.secondary'
              },
              '& .MuiTab-root': {
                color: 'text.secondary',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 400,
                minHeight: 'auto',
                py: 0.75,
                px: 2,
                mr: 0.5,
                '&.Mui-selected': {
                  color: 'text.primary',
                  fontWeight: 700,
                  backgroundColor: 'background.paper',
                  borderRadius: '6px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  borderTop: 'none',
                }
              }
            }}
          >
            <Tab 
              label="General Info" 
              {...a11yProps(0)}
              sx={{
                fontWeight: tabValue === 0 ? 700 : 400,
                color: tabValue === 0 ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === 0 ? 'background.paper' : 'transparent',
                border: tabValue === 0 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: tabValue === 0 ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            {showPocTab && (
            <Tab 
              label="POC" 
              {...a11yProps(1)}
              sx={{
                fontWeight: tabValue === 1 ? 700 : 400,
                color: tabValue === 1 ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === 1 ? 'background.paper' : 'transparent',
                border: tabValue === 1 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: tabValue === 1 ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            )}
            <Tab 
              label="Earnings & Jobs" 
              {...a11yProps(earningsJobsTabIndex)}
              sx={{
                fontWeight: tabValue === earningsJobsTabIndex ? 700 : 400,
                color: tabValue === earningsJobsTabIndex ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === earningsJobsTabIndex ? 'background.paper' : 'transparent',
                border: tabValue === earningsJobsTabIndex ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: tabValue === earningsJobsTabIndex ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            {showActivityTab && (
            <Tab
              label={
                <Badge
                  badgeContent={activitiesCount}
                  color="primary"
                  max={999}
                  sx={{
                    '& .MuiBadge-badge': {
                      right: -14,
                      top: 2,
                    },
                  }}
                >
                  Activity
                </Badge>
              }
              {...a11yProps(activityTabIndex)}
              sx={{
                fontWeight: tabValue === activityTabIndex ? 700 : 400,
                color: tabValue === activityTabIndex ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === activityTabIndex ? 'background.paper' : 'transparent',
                border: tabValue === activityTabIndex ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                pr: 2.5,
                overflow: 'visible',
                '&:hover': {
                  backgroundColor: tabValue === activityTabIndex ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            )}
          </Tabs>
        </Box>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          ...(isSmallScreen
            ? { flex: 1 }
            : lockedContentHeight
              ? {
                  height: lockedContentHeight,
                  maxHeight: '100%',
                  flex: '0 1 auto',
                }
              : { flex: '0 1 auto' }),
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
        {showError && (
          <Box sx={{ p: 3, pb: 0 }}>
            <Alert severity="error" onClose={() => setShowError('')}>
              {showError}
            </Alert>
          </Box>
        )}

        <TabPanel value={tabValue} index={0} contentRef={generalInfoContentRef} keepMounted>
          <Box ref={formRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          <TextField 
            fullWidth 
            required={editMode}
            name="client_name"
            label="Client Name (Real Name)" 
            variant="outlined" 
            slotProps={{ input: { readOnly: !editMode, }, }} 
            value={editClient.client_name || ''}
            error={!!errors.client_name}
            helperText={errors.client_name}
            onChange={(event) => updateEditField('client_name', event.target.value)}
            onBlur={() => {
              if (!editMode) return;
              handleFieldBlur('client_name', editClientRef.current.client_name);
              if (displayAsLinkedRef.current) {
                handleFieldBlur('display_as', editClientRef.current.display_as);
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          <TextField 
            fullWidth 
            required={editMode}
            name="display_as"
            label="Display As"
            value={editClient.display_as || ''} 
            variant="outlined" 
            slotProps={{ input: { readOnly: !editMode, }, }} 
            onChange={(event) => updateEditField('display_as', event.target.value)}
            onBlur={() => editMode && handleFieldBlur('display_as', editClientRef.current.display_as)}
            error={!!errors.display_as}
            helperText={errors.display_as || (editMode ? 'Follows client name until you edit it' : 'Use an alias or code name instead of the real name')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          <ClientInvoicePatternField
            required={editMode}
            value={editClient.invoice_pattern}
            onChange={(invoice_pattern) => updateEditField('invoice_pattern', invoice_pattern)}
            onBlur={() => editMode && handleFieldBlur('invoice_pattern', editClientRef.current.invoice_pattern)}
            error={errors.invoice_pattern}
            excludeClientId={editClient.id || null}
            readOnly={!editMode}
            disabled={!editMode}
          />

          {/* Client Status */}
          <FormControl fullWidth required={editMode} variant="outlined">
            <InputLabel sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'text.secondary',
              '&.Mui-focused': {
                color: isDarkMode ? '#fff' : '#000',
              }
            }}>Status</InputLabel>
            <Select
              value={editClient.client_status}
              label="Status"
              onChange={(event) => updateEditField('client_status', event.target.value)}
              readOnly={!editMode}
              sx={{
                borderRadius: 1,
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              }}
            >
              <MenuItem value={1} sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
              <MenuItem value={0} sx={{ fontSize: '0.875rem' }}>Inactive</MenuItem>
            </Select>
          </FormControl>

          {/* Client color (full color picker; used in Jobs, Accounting, Clients) */}
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Client color
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.75rem' }}>
              Used for this client in Jobs, Accounting, and Clients. Stored on this device.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Box
                component="label"
                htmlFor="client-color-picker"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s, border-color 0.2s',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '6px',
                    backgroundColor: clientColorHex,
                    border: '2px solid',
                    borderColor: isDarkMode ? '#475569' : '#cbd5e1',
                    flexShrink: 0,
                  }}
                />
                {/* <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Choose color
                </Typography> */}
                {/* <PaletteIcon sx={{ fontSize: 20, color: 'text.secondary' }} /> */}
              </Box>
              <Box
                component="input"
                id="client-color-picker"
                type="color"
                value={clientColorHex}
                onChange={(e) => {
                  const hex = e.target.value;
                  setClientColorHex(hex);
                  if (selectedClient?.id != null) setClientColor(user?.company_id, selectedClient.id, hex);
                }}
                aria-label="Open color picker"
                sx={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  opacity: 0,
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              />
              <TextField
                size="small"
                label="Hex"
                value={clientColorHex}
                onChange={(e) => {
                  let v = e.target.value.trim();
                  if (!v.startsWith('#')) v = '#' + v;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(v) || v === '#') {
                    setClientColorHex(v === '#' ? '#' : v);
                    if (v.length === 7 && /^#[0-9A-Fa-f]{6}$/.test(v) && selectedClient?.id != null) {
                      setClientColor(user?.company_id, selectedClient.id, v);
                    }
                  }
                }}
                onBlur={() => {
                  if (selectedClient?.id == null) return;
                  if (/^#[0-9A-Fa-f]{6}$/.test(clientColorHex)) return;
                  if (/^#[0-9A-Fa-f]{3}$/.test(clientColorHex)) {
                    const expanded = '#' + clientColorHex[1] + clientColorHex[1] + clientColorHex[2] + clientColorHex[2] + clientColorHex[3] + clientColorHex[3];
                    setClientColorHex(expanded);
                    setClientColor(user?.company_id, selectedClient.id, expanded);
                    return;
                  }
                  setClientColorHex(getClientColor(selectedClient.id, user?.company_id));
                }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                    '& fieldset': { borderColor: isDarkMode ? '#334155' : '#e0e0e0' },
                  },
                  '& .MuiInputBase-input': { fontSize: '0.875rem', fontFamily: 'monospace' },
                }}
              />
              <Chip
                label="Preview"
                size="small"
                sx={{
                  backgroundColor: getClientChipStylesFromHex(clientColorHex).bgColor,
                  color: getClientChipStylesFromHex(clientColorHex).textColor,
                  border: `1px solid ${getClientChipStylesFromHex(clientColorHex).borderColor}`,
                  fontSize: '0.75rem',
                  height: 24,
                  borderRadius: '6px',
                  '& .MuiChip-label': { padding: '0 8px' },
                }}
              />
            </Box>
          </Box>

          {/* Admins */}
          {canManageClientsDirectly(user) && (
            <Autocomplete 
              multiple 
              id="admins-checkboxes" 
              readOnly={!editMode} 
              disableCloseOnSelect 
              options={admins} 
              getOptionLabel={(option) => option.email}
              value={editClient.admins}
              onChange={(_, value) => updateEditField('admins', value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiAutocomplete-listbox': {
                  borderRadius: 1,
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  backgroundColor: 'background.paper',
                  padding: 0,
                }
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li 
                    key={key} 
                    {...optionProps}
                    style={{
                      ...optionProps.style,
                      padding: '8px 16px',
                      margin: 0,
                      backgroundColor: selected ? (isDarkMode ? '#1e293b' : '#f5f5f5') : 'transparent',
                    }}
                  >
                    <Checkbox 
                      icon={icon} 
                      checkedIcon={checkedIcon} 
                      checked={selected}
                      sx={{ 
                        marginRight: 2,
                        padding: '4px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1rem',
                        }
                      }}
                    />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: selected ? 600 : 400,
                        color: 'text.primary',
                        fontSize: '0.875rem',
                      }}
                    >
                      {option.email}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                      label="Admins" 
                      placeholder="Admins" 
                      fullWidth 
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                          '&.Mui-focused': {
                            color: isDarkMode ? '#fff' : '#000',
                          }
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          color: 'text.primary',
                        }
                      }}
                />
              )}
            />
          )}

            {/* Address */}
          {editMode ? (
            <GooglePlacesAutocomplete
              required
              name="client_address"
              label="Address"
              value={editClient.client_address || ''}
              onChange={(value) => updateEditField('client_address', value)}
              onBlur={() => handleFieldBlur('client_address', editClientRef.current.client_address)}
              onPlaceSelect={handlePlaceSelect}
              error={!!errors.client_address}
              helperText={errors.client_address}
              placeholder="Start typing an address..."
            />
          ) : (
            <TextField 
              fullWidth 
              required={editMode}
              name="client_address"
              label="Address" 
              value={editClient.client_address || ''} 
              variant="outlined" 
              slotProps={{ input: { readOnly: true, }, }} 
              error={!!errors.client_address}
              helperText={errors.client_address}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& fieldset': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.75rem',
                }
              }}
            />
          )}

          {editMode ? (
            <TextField
              fullWidth
              name="address_2"
              label="Address Line 2"
              value={editClient.address_2 || ''}
              onChange={(e) => updateEditField('address_2', e.target.value)}
              onBlur={() => handleFieldBlur('address_2', editClientRef.current.address_2)}
              variant="outlined"
              error={!!errors.address_2}
              helperText={errors.address_2}
              placeholder="Suite, unit, floor, etc."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& fieldset': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.75rem',
                }
              }}
            />
          ) : editClient.address_2 ? (
            <TextField 
              fullWidth 
              label="Address Line 2" 
              value={editClient.address_2 || ''} 
              variant="outlined" 
              slotProps={{ input: { readOnly: true, }, }} 
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& fieldset': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
              }}
            />
          ) : null}

          {/* City, State, and Zip Code */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* City */}
            <Box sx={{ flex: 1 }}>
              {editMode ? (
                <OptimizedCityAutocomplete
                  value={editClient.city || ''}
                  onChange={(value) => updateEditField('city', value || '')}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      required
                      name="city"
                      label="City" 
                      placeholder="Select or type city name" 
                      fullWidth 
                      error={!!errors.city}
                      helperText={errors.city}
                      onBlur={(event) => {
                        const typed = event.target.value || '';
                        if (typed !== (editClientRef.current.city || '')) {
                          updateEditField('city', typed);
                        }
                        handleFieldBlur('city', typed);
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          color: 'text.secondary',
                          '&.Mui-focused': {
                            color: isDarkMode ? '#fff' : '#000',
                          }
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          color: 'text.primary',
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '0.75rem',
                        }
                      }}
                    />
                  )}
                />
              ) : (
                <TextField 
                  fullWidth 
                  label="City" 
                  value={editClient.city || ''} 
                  variant="outlined" 
                  slotProps={{ input: { readOnly: true, }, }} 
                  error={!!errors.city}
                  helperText={errors.city}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      '& fieldset': {
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDarkMode ? '#fff' : '#000',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      '&.Mui-focused': {
                        color: isDarkMode ? '#fff' : '#000',
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      color: 'text.primary',
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '0.75rem',
                    }
                  }}
                />
              )}
            </Box>

            {/* State */}
            <Autocomplete
              options={Object.keys(states)}
              value={editClient.state || ''}
              onChange={(event, value) => updateEditField('state', value || '')}
              getOptionLabel={(option) => option ? `${states[option]} - ${option}` : ''}
              readOnly={!editMode}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiAutocomplete-listbox': {
                  borderRadius: 1,
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  backgroundColor: 'background.paper',
                  padding: 0,
                }
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li 
                    key={key} 
                    {...optionProps}
                    style={{
                      ...optionProps.style,
                      padding: '8px 16px',
                      margin: 0,
                      backgroundColor: selected ? (isDarkMode ? '#1e293b' : '#f5f5f5') : 'transparent',
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: selected ? 600 : 400,
                        color: 'text.primary',
                        fontSize: '0.875rem',
                      }}
                    >
                      {states[option]} - {option}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  required={editMode}
                  name="state"
                  label="State" 
                  placeholder="Select state" 
                  fullWidth 
                  error={!!errors.state}
                  helperText={errors.state}
                  onBlur={() => editMode && handleFieldBlur('state', editClientRef.current.state)}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      '&.Mui-focused': {
                        color: isDarkMode ? '#fff' : '#000',
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      color: 'text.primary',
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '0.75rem',
                    }
                  }}
                />
              )}
            />

            {/* Zip Code */}
            <TextField 
              fullWidth 
              required={editMode}
              name="zip_code"
              label="Zip Code" 
              value={editClient.zip_code || ''} 
              variant="outlined" 
              slotProps={{ input: { readOnly: !editMode, }, }} 
              onChange={(event) => updateEditField('zip_code', formatZipCode(event.target.value))}
              onBlur={() => editMode && handleFieldBlur('zip_code', editClientRef.current.zip_code)}
              error={!!errors.zip_code}
              helperText={errors.zip_code}
              InputProps={{
                readOnly: !editMode,
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                    </Box>
                  </InputAdornment>
                ),
              }}
              placeholder="12345 or 12345-6789"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& fieldset': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.75rem',
                }
              }}
            />
          </Box>

          {/* Email */}
          <TextField 
            fullWidth 
            name="email"
            label="Email" 
            variant="outlined" 
            slotProps={{ input: { readOnly: !editMode, }, }} 
            value={editClient.email || ''}
            onChange={(event) => updateEditField('email', event.target.value)}
            onBlur={() => editMode && handleFieldBlur('email', editClientRef.current.email)}
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Phone */}
          <TextField 
            fullWidth 
            required={editMode}
            name="phone"
            label="Phone" 
            value={editClient.phone || ''} 
            variant="outlined" 
            onChange={(event) => updateEditField('phone', formatPhoneNumber(event.target.value))}
            onBlur={() => editMode && handleFieldBlur('phone', editClientRef.current.phone)}
            error={!!errors.phone}
            helperText={errors.phone}
            InputProps={{
              readOnly: !editMode,
            }}
            placeholder="(555) 123-4567"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

            {/* <TextField fullWidth margin="dense" label="Client Admin Email" value={selectedClient.client_admin_email || ''} variant="outlined" slotProps={{  input: {    readOnly: true,  },}} sx={{ marginBottom: 2 }}/> */}

          </Box>
        </TabPanel>

        {/* POC Tab */}
        {showPocTab && (
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Header with Add Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>
                Points of Contact
              </Typography>
              {canManageClientPocs(user) && (
                <Button
                  variant="contained"
                  onClick={handleAddPoc}
                  sx={{
                    backgroundColor: isDarkMode ? '#fff' : '#000',
                    color: isDarkMode ? '#000' : '#fff',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 3,
                    py: 1.25,
                    borderRadius: 1.5,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      boxShadow: 'none',
                    }
                  }}
                >
                  + Add POC
                </Button>
              )}
            </Box>

            {/* Error Message */}
            {pocsError && (
              <Box sx={{ p: 2, backgroundColor: isDarkMode ? '#7f1d1d' : '#ffebee', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: 'error.main', fontSize: '0.875rem' }}>
                  {pocsError}
                </Typography>
              </Box>
            )}

            {/* Loading State */}
            {loadingPocs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : pocs.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  No POCs found for this client.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ 
                maxHeight: '500px', 
                borderRadius: 1,
                border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem',
                        backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                        color: 'text.primary',
                        borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                      }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem',
                        backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                        color: 'text.primary',
                        borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                      }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem',
                        backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                        color: 'text.primary',
                        borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                      }}>
                        Phone
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem',
                        backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                        color: 'text.primary',
                        borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                      }}>
                        Title
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem',
                        backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                        color: 'text.primary',
                        borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                      }}>
                        Status
                      </TableCell>
                      {canManageClientPocs(user) && (
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          fontSize: '0.875rem',
                          backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                          color: 'text.primary',
                          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                        }}>
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pocs.map((poc) => (
                      <TableRow key={poc.id} hover>
                        <TableCell sx={{ 
                          fontSize: '0.875rem',
                          color: 'text.primary',
                          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                        }}>
                          {poc.name}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: '0.875rem',
                          color: 'text.primary',
                          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                        }}>
                          {poc.email || '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: '0.875rem',
                          color: 'text.primary',
                          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                        }}>
                          {poc.phone || '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: '0.875rem',
                          color: 'text.primary',
                          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                        }}>
                          {poc.title || '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: '0.875rem',
                          color: 'text.primary',
                          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                        }}>
                          {poc.is_primary && (
                            <Chip
                              label="Primary"
                              size="small"
                              sx={{
                                backgroundColor: isDarkMode ? '#fff' : '#000',
                                color: isDarkMode ? '#000' : '#fff',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                height: '24px',
                                borderRadius: '4px',
                                '& .MuiChip-label': {
                                  padding: '0 8px',
                                }
                              }}
                            />
                          )}
                        </TableCell>
                        {canManageClientPocs(user) && (
                          <TableCell sx={{ 
                            fontSize: '0.875rem',
                            color: 'text.primary',
                            borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
                          }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditPoc(poc)}
                                sx={{
                                  color: 'text.secondary',
                                  padding: '4px',
                                  '&:hover': {
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeletePoc(poc)}
                                sx={{
                                  color: 'error.main',
                                  padding: '4px',
                                  '&:hover': {
                                    backgroundColor: isDarkMode ? 'rgba(211, 47, 47, 0.08)' : 'rgba(211, 47, 47, 0.04)'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
        )}

        {/* Earnings & Jobs Tab */}
        <TabPanel value={tabValue} index={earningsJobsTabIndex}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Date range filter: single calendar dropdown (start/end in one picker) */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <DateRangePickerDropdown
                label="Date range"
                placeholder="Select date range"
                startDate={dateStart}
                endDate={dateEnd}
                onStartChange={setDateStart}
                onEndChange={setDateEnd}
                isDarkMode={isDarkMode}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setDateStart(null);
                  setDateEnd(null);
                }}
                disabled={!dateStart && !dateEnd}
                sx={{
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  px: 3,
                  py: 1.25,
                  borderRadius: 1,
                  '&:hover': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                    backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                  },
                  '&:disabled': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                    // Override MUI's default disabled opacity so the label stays readable in dark mode.
                    opacity: 1,
                    color: isDarkMode ? theme.palette.text.secondary : theme.palette.text.disabled,
                    backgroundColor: isDarkMode ? 'rgba(51, 65, 85, 0.35)' : 'transparent',
                  }
                }}
              >
                Clear Filters
              </Button>
            </Box>

            <ClientEarningsSummaryPanel
              data={earningsOverview}
              loading={loadingEarnings}
              error={earningsError}
              rangeLabel={earningsRangeLabel}
              onMetricClick={handleEarningsMetricClick}
            />

            <ClientPaidUnpaidPanel
              data={earningsOverview}
              loading={loadingEarnings}
              error={earningsError}
              rangeLabel={earningsRangeLabel}
              onPaidClick={() => handleEarningsMetricClick('invoiced_paid')}
              onUnpaidClick={() => handleEarningsMetricClick('invoiced_unpaid')}
            />

            <ClientJobsStatusChips
              statusCounts={earningsOverview?.statusCounts || []}
              loading={loadingEarnings}
              error={earningsError}
              selectedStatus={drilldownStatusId}
              onStatusClick={handleEarningsStatusClick}
            />
          </Box>
        </TabPanel>

        {/* Activity Tab (owner / GM) */}
        {showActivityTab && (
        <TabPanel value={tabValue} index={activityTabIndex}>
          <ActivityTab
            logs={activityLogs}
            loadingLogs={activityLoading}
            error={activityError}
          />
        </TabPanel>
        )}

        </Box>
      </DialogContent>

      {/* Dialog Actions — only on General Info (edit is scoped to that tab) */}
      {showGeneralInfoActions && (editMode || canManageClientsDirectly(user)) && (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2,
        p: 3, 
        borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        backgroundColor: 'background.paper',
        flexShrink: 0,
      }}>
        {showError && (
          <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600, mr: 'auto' }}>
            {showError}
          </Typography>
        )}
        
        {editMode ? (
          <>
            <Button 
              variant="outlined"
              onClick={() => {
                setEditClient(selectedClient);
                setEditMode(false);
              }}
              sx={{ 
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1,
                '&:hover': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                }
              }}
            >
              Cancel
            </Button>
            
            <Button 
              disabled={loading} 
              variant="contained" 
              onClick={handleUpdateClient}
              sx={{ 
                backgroundColor: isDarkMode ? '#fff' : '#000',
                color: isDarkMode ? '#000' : 'white',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: isDarkMode ? '#475569' : '#666',
                  color: isDarkMode ? '#94a3b8' : '#fff',
                }
              }}
            >
              Save Changes
            </Button>
          </>
        ) : (
          <Button 
            variant="contained" 
            onClick={() => {
              setEditMode(true);
            }}
            sx={{ 
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : 'white',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              borderRadius: 1,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                boxShadow: 'none',
              }
            }}
          >
            Edit
          </Button>
        )}
      </Box>
      )}

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Client updated
        </Alert>
      </Snackbar>

      {/* POC Success Toast */}
      <Snackbar
        open={showPocSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowPocSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowPocSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          POC {editingPoc ? 'updated' : 'created'} successfully
        </Alert>
      </Snackbar>

      {/* POC Form Dialog */}
      <PocFormDialog
        open={pocFormOpen}
        onClose={() => {
          setPocFormOpen(false);
          setEditingPoc(null);
        }}
        onSave={handleSavePoc}
        poc={editingPoc}
        loading={savingPoc}
        clientId={selectedClient?.id}
      />

      <ClientJobsDrilldownDialog
        open={Boolean(drilldownMetric) || drilldownStatusId != null}
        clientId={selectedClient?.id}
        metric={drilldownMetric}
        statusId={drilldownStatusId}
        statusLabel={drilldownStatusLabel}
        rangeLabel={earningsRangeLabel}
        queryParams={earningsQueryParams}
        onClose={closeDrilldown}
        onOpenJob={handleOpenJobFromEarnings}
      />
    </Dialog>

    {selectedJob && (
      <JobDetailsDialog
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        user={user}
      />
    )}
    </>
  )
}